import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchThreadsOrganized, createThread, fetchTopics, type Thread, type TopicThreadsResponse, type SubthreadWithThreads } from '../../features/topics/api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { AdBanner } from '../../components/ads/AdBanner';
import { MessageSquare, Eye, Clock, Pin, Lock, TrendingUp } from 'lucide-react';
import { isAdmin } from '../../services/authService';
import { ImageUpload } from '../../components/upload/ImageUpload';

interface ThreadStats {
  replyCount?: number;
  viewCount?: number;
  lastPostAt?: string;
  lastPostBy?: string;
}

type EnhancedThread = Thread & ThreadStats;

export function DiscuzTopicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [organizedThreads, setOrganizedThreads] = useState<TopicThreadsResponse | null>(null);
  const [topicId, setTopicId] = useState<string>('');
  const [topicTitle, setTopicTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const threadsPerPage = 20;
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      loadTopicData();
    }
  }, [slug]);

  async function loadTopicData() {
    if (!slug) return;

    try {
      setLoading(true);

      // First, fetch all topics and find the one matching the slug
      const allTopics = await fetchTopics(100);
      const topic = allTopics.find(t => t.slug === slug);

      if (!topic) {
        setError('Topic not found');
        setLoading(false);
        return;
      }

      setTopicId(topic.id);
      setTopicTitle(topic.title);

      // Fetch organized threads using the topic ID
      const threadsData = await fetchThreadsOrganized(topic.id, 100);
      setOrganizedThreads(threadsData);

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load topic data');
    } finally {
      setLoading(false);
    }
  }

  const totalThreadCount = organizedThreads
    ? organizedThreads.subthreads.reduce((sum, st) => sum + st.threads.length, 0) + organizedThreads.regularThreads.length
    : 0;

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    // For 7+ days, show date and time
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-slate-600 dark:text-slate-400">Loading threads...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
          Error: {error}
        </div>
        <Button onClick={loadTopicData} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-base mb-6">
        <button
          onClick={() => navigate('/forum')}
          className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium transition-colors"
        >
          Forum
        </button>
        <span className="text-slate-400 dark:text-slate-500">&gt;</span>
        <span className="text-slate-700 dark:text-slate-300 font-medium">{topicTitle || 'Loading...'}</span>
      </nav>

      {/* Header */}
      <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-t-xl p-6 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl flex items-center justify-center shadow-glow">
            <MessageSquare className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{topicTitle || 'Loading...'}</h1>
            <p className="text-base text-slate-600 dark:text-slate-400 mt-0.5">{totalThreadCount} threads</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          New Thread
        </Button>
      </div>

      {/* Create Thread Modal */}
      {topicId && (
        <Modal
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          title="Create New Thread"
          size="xl"
        >
          <CreateThreadForm
            topicId={topicId}
            availableSubthreads={organizedThreads?.subthreads || []}
            onSuccess={() => {
              setShowCreateForm(false);
              loadTopicData();
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        </Modal>
      )}

      {/* Display active ads */}
      {topicId && (
        <div className="my-4">
          <AdBanner topicId={topicId} type="banner" />
        </div>
      )}

      {/* Discuz-style Thread Display */}
      {!organizedThreads || totalThreadCount === 0 ? (
        <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border border-t-0 rounded-b-xl p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">No threads yet. Start a discussion!</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border border-t-0 rounded-b-xl overflow-hidden">
          {/* Subthreads Section */}
          {organizedThreads.subthreads.length > 0 && (
            <div className="border-b border-slate-200 dark:border-dark-border">
              <div className="bg-gradient-to-r from-brand-50 to-accent-50 dark:from-brand-950/30 dark:to-accent-950/30 px-6 py-3 border-b border-brand-200 dark:border-brand-900/50">
                <h2 className="text-base font-semibold text-brand-700 dark:text-brand-300 uppercase tracking-wide">📌 Pinned Subthreads</h2>
              </div>
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-dark-border text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                <div className="col-span-5">Thread</div>
                <div className="col-span-3 text-right">Author</div>
                <div className="col-span-2 text-right">Stats</div>
                <div className="col-span-2 text-right">Last Reply</div>
              </div>
              {organizedThreads.subthreads.map((subthreadData) => (
                <ThreadRow key={subthreadData.subthread.id} thread={subthreadData.subthread} navigate={navigate} formatRelativeTime={formatRelativeTime} />
              ))}
            </div>
          )}

          {/* Regular Threads Section */}
          {organizedThreads.regularThreads.length > 0 && (
            <div>
              <div className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-dark-bg-secondary dark:to-dark-card px-6 py-3 border-b border-slate-200 dark:border-dark-border">
                <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">💬 General Discussions</h2>
              </div>
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-dark-border text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                <div className="col-span-5">Thread</div>
                <div className="col-span-3 text-right">Author</div>
                <div className="col-span-2 text-right">Stats</div>
                <div className="col-span-2 text-right">Last Reply</div>
              </div>
              {(() => {
                const startIndex = (currentPage - 1) * threadsPerPage;
                const endIndex = startIndex + threadsPerPage;
                const paginatedThreads = organizedThreads.regularThreads.slice(startIndex, endIndex);
                const totalPages = Math.ceil(organizedThreads.regularThreads.length / threadsPerPage);

                return (
                  <>
                    {paginatedThreads.map((thread) => (
                      <ThreadRow key={thread.id} thread={thread} navigate={navigate} formatRelativeTime={formatRelativeTime} />
                    ))}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 p-6 border-t border-slate-200 dark:border-dark-border bg-slate-50 dark:bg-slate-800/30">
                        <Button
                          variant="secondary"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          ← Previous
                        </Button>
                        <span className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="secondary"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next →
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ThreadRow({
  thread,
  navigate,
  formatRelativeTime,
  isNested = false,
}: {
  thread: EnhancedThread;
  navigate: (path: string) => void;
  formatRelativeTime: (date: string) => string;
  isNested?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-12 gap-4 p-5 border-b border-slate-200 dark:border-dark-border last:border-b-0 hover:bg-slate-50 dark:hover:bg-dark-hover transition-all group ${
        isNested ? 'pl-16 bg-slate-50/50 dark:bg-dark-bg-secondary/50' : ''
      }`}
    >
      {/* Column 1: Title (40%) */}
      <div className="col-span-5 flex items-start gap-3">
        {/* Thread Icon */}
        <div className="flex-shrink-0">
          {thread.pinned ? (
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 border border-brand-400 dark:border-brand-600 flex items-center justify-center shadow-sm">
              <Pin className="w-5 h-5 text-white" />
            </div>
          ) : thread.locked ? (
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600 border border-yellow-400 dark:border-yellow-600 flex items-center justify-center shadow-sm">
              <Lock className="w-5 h-5 text-white" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-dark-card border border-slate-300 dark:border-dark-border flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {thread.pinned && (
              <span className="px-2 py-0.5 bg-brand-100 dark:bg-brand-900/30 border border-brand-300 dark:border-brand-800 rounded text-xs font-medium text-brand-700 dark:text-brand-400">
                Sticky
              </span>
            )}
            {thread.locked && (
              <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-800 rounded text-xs font-medium text-yellow-700 dark:text-yellow-400">
                Locked
              </span>
            )}
          </div>
          <button
            onClick={() => navigate(`/forum/thread/${thread.id}`)}
            className="text-left w-full"
          >
            <h3 className="text-base font-semibold text-slate-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-2">
              {thread.title}
            </h3>
          </button>
        </div>
      </div>

      {/* Column 2: Creator & Create Date (25%) */}
      <div className="col-span-3 flex flex-col justify-center text-sm text-right">
        <div className="text-slate-900 dark:text-slate-100 font-medium">{thread.authorAlias}</div>
        <div className="text-slate-500 dark:text-slate-500 text-xs mt-1">
          {new Date(thread.createdAt).toLocaleDateString()}
        </div>
      </div>

      {/* Column 3: Reply & View Count (15%) */}
      <div className="col-span-2 flex flex-col justify-center gap-2 text-sm text-right">
        <div className="flex items-center gap-2 justify-end">
          <MessageSquare className="w-4 h-4 text-slate-500 dark:text-slate-500" />
          <span className="font-semibold text-slate-900 dark:text-slate-100">{thread.replyCount || 0}</span>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Eye className="w-4 h-4 text-slate-500 dark:text-slate-500" />
          <span className="font-semibold text-slate-900 dark:text-slate-100">{thread.viewCount || 0}</span>
        </div>
      </div>

      {/* Column 4: Last Reply (20%) */}
      <div className="col-span-2 flex flex-col justify-center text-sm text-right">
        {thread.lastCommentAlias && thread.lastCommentTime ? (
          <>
            <div className="text-slate-900 dark:text-slate-100 font-medium truncate">{thread.lastCommentAlias}</div>
            <div className="text-slate-500 dark:text-slate-500 text-xs mt-1">
              {formatRelativeTime(thread.lastCommentTime)}
            </div>
          </>
        ) : (
          <div className="text-slate-400 dark:text-slate-500 italic text-xs">No replies yet</div>
        )}
      </div>
    </div>
  );
}

function CreateThreadForm({
  topicId,
  availableSubthreads,
  onSuccess,
  onCancel,
}: {
  topicId: string;
  availableSubthreads: SubthreadWithThreads[];
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [alias, setAlias] = useState('');
  const [body, setBody] = useState('');
  const [isSubthread, setIsSubthread] = useState(false);
  const [parentSubthreadId, setParentSubthreadId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !alias.trim() || !body.trim()) {
      setError('All fields are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await createThread(topicId, {
        title: title.trim(),
        body: body.trim(),
        attachments,
        attachmentsFiles: attachmentFiles,
        isSubthread: isSubthread,
        parentSubthreadId: parentSubthreadId || undefined,
      });

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Thread Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            placeholder="What's your thread about?"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Your Alias *</label>
          <input
            type="text"
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            placeholder="Anonymous name for this topic"
            required
          />
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">Choose an alias for this discussion</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Initial Post *</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            rows={6}
            placeholder="Start the discussion..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Attachments (optional)</label>
          <ImageUpload
            onUpload={(files) => setAttachmentFiles((prev) => [...prev, ...files])}
            onChange={setAttachments}
            maxFiles={5}
            accept="image/*"
          />
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">Upload up to 5 images to include with your first post.</p>
        </div>

      <div className="border-t border-slate-200 dark:border-dark-border pt-5">
        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Thread Options</h3>

          {/* Checkbox for creating subthread (admin only) */}
          {isAdmin() && (
            <div className="mb-4">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isSubthread}
                  onChange={(e) => {
                    setIsSubthread(e.target.checked);
                    // Clear parent subthread if making this a subthread
                    if (e.target.checked) {
                      setParentSubthreadId('');
                    }
                  }}
                  className="w-4 h-4 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded focus:ring-2 focus:ring-brand-500"
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Create as Subthread
                </span>
              </label>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5 ml-7">
                Subthreads are pinned containers that can hold multiple regular threads
              </p>
            </div>
          )}

          {/* Dropdown for selecting parent subthread (only if NOT creating a subthread) */}
          {!isSubthread && availableSubthreads.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Post in Subthread (Optional)
              </label>
              <select
                value={parentSubthreadId}
                onChange={(e) => setParentSubthreadId(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
              >
                <option value="">None - Post as regular thread</option>
                {availableSubthreads.map((st) => (
                  <option key={st.subthread.id} value={st.subthread.id}>
                    {st.subthread.title}
                  </option>
                ))}
              </select>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">
                Choose a subthread to organize your discussion
              </p>
            </div>
          )}
      </div>

      <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-dark-border">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Thread'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
